import React, { useContext, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import Table from "@components/Table/Table";
import errorIcon from '@images/error.svg';
import { DataSourceType, IDataSource, IQuery, IQueryEditorProps, ISourceSchema, IStep, Param, StepType } from "@types";
import { QueryBuilderContext } from "@query-builder/QueryBuilderContext";
import { InitialSourceId, appendSchema, findNextItemId, findNextStepId, getQueryBySelectedStepId, getQueryForSelectedSource, getRegisteredStepByType, getSchemaForSource, getSchemaForStep, validateParam, validateParams, validateQuery, validateStep } from "@utils";
import { checkStatus, executeQuery, exportCsv } from "@query-engine-service";
import { StepConfigurator } from "../StepConfigurator/StepConfigurator";
import { StepSelector } from "@components/StepConfigurator/StepSelector/StepSelector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { applyFormatters, AsyncButton, Button, hasValue, Input, Select, useAlert, useToast } from "uxp/components";
import { Loading } from "@components/Loading/Loading";
import { Copilot } from "@components/Copilot/Copilot";
import { renderIcon } from "@components/DataSourceBrowser/DataSourceBrowser";
import { SortableList } from "@components/SortableList/SortableList";
import { PieChartPreview } from "./visualizers/PieChartPreview";
import { BarChartPreview } from "./visualizers/BarChartPreview";
import { isSourceConfigurable, ParamsEditor } from "./ParamEditor";
import { FormSection } from "@components/FormSection/FormSection";
import { DataSourceBrowserModal } from "@components/DataSourceBrowser/DataSourceBrowserModal";
import _ from 'lodash';
import '@components/StepConfigurator/Steps/steps';
import './QueryEditor.scss';

export const QueryEditor: React.FunctionComponent<IQueryEditorProps> = (props) => {

    const { query, onChange } = props
    const { sources, steps, sourceOnly } = query

    const context = useContext(QueryBuilderContext)
    const alerts = useAlert()
    const toast = useToast()

    const [selectedStep, setSelectedStep] = useState<string>(null)
    const [selectedStepData, setSelectedStepData] = useState<any>(null)
    const [selectedStepDataError, setSelectedStepDataError] = useState<string>(null);
    const [loadingStepData, setLoadingStepData] = useState(true)
    const [previewMode, setPreviewMode] = useState('table')
    const [selectedTab, setSelectedTab] = useState<'pipeline' | 'datasources' | 'params'>('pipeline')
    const [supportParams, setSupportParams] = useState(false);
    const [showSourceBrowser, setShowSourceBrowser] = useState(false);

    const previewUpdateTimerRef = useRef(null)
    const schemaUpdateTimerRef = useRef(null)
    const hasChanged = useRef(false)
    const nextStepIdRef = useRef(null)

    useEffect(() => {
        // initial schema update if a query is passed 
        if (query) {
            updateSchema(query);
        }

        setSelectedTab('pipeline')
    }, []);

    useEffect(() => {
        if (!!query && (!hasValue(selectedStep) || hasValue(nextStepIdRef.current))) {
            if (hasValue(nextStepIdRef.current)) {
                setSelectedStep(nextStepIdRef.current)
                nextStepIdRef.current = null
            }
            else {
                const _steps = query?.steps || []
                setSelectedStep(_steps.length > 0 ? _steps[_steps.length - 1].id : InitialSourceId)
            }
        }
    }, [query]);

    useEffect(() => {
        if (!!selectedStep) {
            hasChanged.current = true
            if (previewUpdateTimerRef.current) clearTimeout(previewUpdateTimerRef.current);
            previewUpdateTimerRef.current = setTimeout(getStepData, 1000)
        }
    }, [selectedStep, query])

    useEffect(() => {
        console.log('query_________', query)
    }, [query])

    useEffect(() => {
        checkStatus(context)
            .then(res => {
                setSupportParams(res?.details?.supportParams || false)
            })
    }, [])

    async function updateSchema(_query: IQuery) {
        if (selectedStep == InitialSourceId) return
        // update schema for the step 
        const schema = await getSchemaForStep(context, _query, selectedStep)
        let updatedQuery = appendSchema(_query, schema, selectedStep)

        // validate steps after the current step 
        // generate schem for steps after the current step 
        const selectedStepIndex = updatedQuery?.steps?.findIndex(s => s.id == selectedStep)
        if (selectedStepIndex >= 0 && selectedStepIndex < updatedQuery?.steps?.length - 1) {
            const stepsAfterCurrentStep = updatedQuery?.steps?.slice(selectedStepIndex)

            for (const s of stepsAfterCurrentStep) {
                const schema = await getSchemaForStep(context, updatedQuery, s.id)
                updatedQuery = appendSchema(updatedQuery, schema, s.id)
            }
        }

        onChange(updatedQuery)
    }

    async function beforeChangeQuery(_query: IQuery) {
        // wait and update schema for the step 
        if (schemaUpdateTimerRef.current) clearTimeout(schemaUpdateTimerRef.current);
        schemaUpdateTimerRef.current = setTimeout(() => { updateSchema(_query) }, 500)

        // commit the changes to reflect immidiately in the ui
        onChange(_query)
    }

    async function removeStep(step: IStep) {
        try {
            const isConfirmed = await alerts.confirm('You are about to remove a step, this cannot be undone. do you wish to continue?')

            if (isConfirmed) {
                const filteredSteps = query.steps.filter(s => s.id !== step.id)

                // if the step is selected 
                if (selectedStep == step.id) {
                    const nextStepId = findNextStepId(query, selectedStep)
                    nextStepIdRef.current = nextStepId
                }

                // commit the changes 
                beforeChangeQuery({ ...query, steps: filteredSteps })
            }
        } catch (e) {

        }
    }

    async function removeSource(source: IDataSource) {
        try {
            const isConfirmed = await alerts.confirm('You are about to remove a datasource, this cannot be undone. do you wish to continue?')

            if (isConfirmed) {

                const _sources = [...query?.sources || []]
                const index = _sources.findIndex(s => s.id == source.id)
                if (index < 0) return

                _sources.splice(index, 1)
                // if the step is selected 
                if (selectedStep == source.id) {
                    const nextSourceId = findNextItemId(query.sources, index);
                    nextStepIdRef.current = nextSourceId
                }

                // commit the changes 
                beforeChangeQuery({ ...query, sources: _sources })
            }
        } catch (e) {

        }
    }

    async function removeParam(param: Param) {
        try {
            const isConfirmed = await alerts.confirm('You are about to remove a parameter, this cannot be undone. do you wish to continue?')

            if (isConfirmed) {
                const _params = [...query?.params || []]
                const index = _params?.findIndex(s => s.id == param.id)
                if (index < 0) return

                _params.splice(index, 1)
                if (selectedStep == param.id) {
                    const nextParamId = findNextItemId(query.params || [], index);
                    nextStepIdRef.current = nextParamId
                }

                // commit the changes 
                beforeChangeQuery({ ...query, params: _params })
            }
        } catch (e) {

        }
    }

    async function changeSource(newSource: IDataSource) {
        // get source schema
        const schema = await getSchemaForSource(context, { sources: [newSource], steps: [] }, newSource.id);
        newSource.schema = schema;

        // Update the first source in the query
        const updatedQuery = _.cloneDeep(query);
        updatedQuery.sources[0] = newSource;

        // Ask user what to do with existing steps
        if (updatedQuery.steps.length > 0 && query?.sources?.[0]?.id !== newSource?.id) {
            const choice = await new Promise<'keep' | 'remove'>((resolve) => {
                alerts.confirm(
                    {
                        title: 'Change Data Source',
                        content: 'You have existing steps in your query. What would you like to do?',
                        confirmButtonTitle: 'Remove Steps',
                        cancelButtonTitle: 'Keep Steps',
                    }
                ).then((result) => {
                    if (result === true) resolve('remove');
                    else if (result === false) resolve('keep');
                }).catch(() => resolve('keep'));
            });

            if (choice === 'remove') {
                updatedQuery.steps = [];
                setSelectedStep(InitialSourceId);
            }
        }

        onChange(updatedQuery);
        setShowSourceBrowser(false);
    }

    async function getStepData() {
        const _query = (selectedTab == 'datasources')
            ? getQueryForSelectedSource(query, selectedStep)
            : getQueryBySelectedStepId(query, selectedStep)

        const { isValid } = validateQuery(_query)

        if (!isValid) {
            setSelectedStepDataError('Query contains one or more incompleted steps')
            return
        }

        setLoadingStepData(true)
        hasChanged.current = false

        /**
         * Configure default parameter values for pipeline preview.
         * 
         * When setting up a data pipeline, users define parameters with default values.
         * During configuration/preview mode, we need to substitute these parameters
         * with their default values so users can see sample data output.
         * 
        */
        if (query?.params?.length > 0) {
            _query.configuredParams = query.params.reduce((final, p) => {
                final[p.name] = hasValue(p.defaultValue)
                    ? applyFormatters(p.defaultValue, [p?.type || 'string'])
                    : null;
                return final;
            }, {} as Record<string, any>)
        }

        const { data, error } = await executeQuery(context, _query)
        setSelectedStepDataError(error || null);
        setSelectedStepData(data?.result || [])
        setLoadingStepData(false)
    }

    function handleStepSelector(type: StepType) {

        let prevSource = '';
        const prevStep = query?.steps?.[query?.steps?.length - 1];
        if (prevStep) {
            prevSource = prevStep.id;
        }
        if (!prevStep) {
            const firstSource = query?.sources?.[0] || null;
            if (!!firstSource) {
                prevSource = firstSource.id;
            }
        }

        const id = `${type}-${Date.now()}`
        const name = type?.toUpperCase()

        const registeredStep = getRegisteredStepByType(type)
        if (!registeredStep) return toast.error('Invalid step selected')

        const newStep: IStep = registeredStep.onSelectStep({ id, name, schema: [], type }, prevSource)
        const _query = Object.assign({}, query)
        _query.steps.push(newStep)
        onChange(_query)
        setSelectedStep(id)
    };

    function renderInitialSourceMessage() {
        if (selectedStep !== InitialSourceId) return null;
        const source = query?.sources?.[0] || null;
        if (!source) return null;

        function updateInitialSource(updatedSource: IDataSource) {
            const _query = Object.assign({}, query);
            _query.sources[0] = updatedSource
            onChange({ ..._query });
        }

        const { isConfigurable } = isSourceConfigurable(source)

        return (<div className='lucy-query-engine-step-configurator-container'>
            <div className="lucy-query-engine-step-configurator-header">
                <div className={classNames("query-editor-step-type-ico-container")}>
                    {renderIcon(source?.type)}
                </div>
                Source
            </div>
            <div className="lucy-query-engine-step-configurator-description">Initial source</div>

            <div className="query-editor-configuration-message-container">

                <div className="query-editor-source-details-section">
                    <div className="query-editor-source-details-label">Name</div>
                    <div className="query-editor-source-details-value">
                        {source.name} &nbsp;&nbsp;&nbsp;&nbsp;
                        <Button
                            title="Change source"
                            icon="fas repeat"
                            onClick={() => setShowSourceBrowser(true)}
                        />
                    </div>
                </div>

                {
                    hasValue(source?.description)
                        ? <div className="query-editor-source-details-section">
                            <div className="query-editor-source-details-label">Description</div>
                            <div className="query-editor-source-details-value">{source.description}</div>
                        </div>
                        : null
                }

                <div className="query-editor-source-details-section">
                    <div className="query-editor-source-details-label">Type</div>
                    <div className="query-editor-source-details-value" style={{ textTransform: 'uppercase' }} >{source.type}</div>
                </div>

                {
                    source.type == DataSourceType.COLLECTION
                        ? <div className="query-editor-source-details-section">
                            <div className="query-editor-source-details-label">Model</div>
                            <div className="query-editor-source-details-value">{source?.model}</div>
                        </div>
                        : null
                }

                {
                    isConfigurable
                        ? <div className="query-editor-source-details-section">
                            <div className="query-editor-source-details-label">Configure Params</div>
                            <div className="query-editor-source-details-value">
                                <ParamsEditor
                                    source={source}
                                    onChange={updateInitialSource}
                                    query={query}
                                />
                            </div>
                        </div>
                        : null
                }

                {
                    !sourceOnly
                        ? <div className='lucy-query-editor-info'>
                            <div className="query-editor-configuration-message">You have selected a data source. You can now add steps to manipulate the data. </div>
                            <div className="query-editor-configuration-message">Click on the "Add Step" button to get started</div>
                        </div>
                        : null
                }
            </div>
        </div>

        );
    }

    function renderConfiguration() {
        if (!selectedStep || selectedStep == InitialSourceId) return null
        return <StepConfigurator
            query={query}
            onChange={beforeChangeQuery}
            selectedStepId={selectedStep}
            context={context}
        />
    }

    function renderSourceDetails() {
        const source = (query?.sources || []).find(s => s.id == selectedStep);
        if (!source) return null;

        function updateSource(updatedSource: IDataSource) {
            const _query = Object.assign({}, query);
            const index = _query.sources.findIndex(s => s.id == selectedStep)
            if (index < 0) return
            _query.sources.splice(index, 1, updatedSource)
            onChange({ ..._query });
        }

        const { isConfigurable } = isSourceConfigurable(source)

        return (<div className='lucy-query-engine-step-configurator-container'>
            <div className="lucy-query-engine-step-configurator-header">
                <div className={classNames("query-editor-step-type-ico-container")}>
                    {renderIcon(source?.type)}
                </div>
                Source
            </div>
            <div className="query-editor-configuration-message-container">

                <div className="query-editor-source-details-section">
                    <div className="query-editor-source-details-label">Name</div>
                    <div className="query-editor-source-details-value">{source.name}</div>
                </div>

                {
                    hasValue(source?.description)
                        ? <div className="query-editor-source-details-section">
                            <div className="query-editor-source-details-label">Description</div>
                            <div className="query-editor-source-details-value">{source.description}</div>
                        </div>
                        : null
                }

                <div className="query-editor-source-details-section">
                    <div className="query-editor-source-details-label">Type</div>
                    <div className="query-editor-source-details-value" style={{ textTransform: 'uppercase' }} >{source.type}</div>
                </div>

                {
                    source.type == DataSourceType.COLLECTION
                        ? <div className="query-editor-source-details-section">
                            <div className="query-editor-source-details-label">Model</div>
                            <div className="query-editor-source-details-value">{source?.model}</div>
                        </div>
                        : null
                }

                {
                    isConfigurable
                        ? <div className="query-editor-source-details-section">
                            <div className="query-editor-source-details-label">Configure Params</div>
                            <div className="query-editor-source-details-value">
                                <ParamsEditor
                                    source={source}
                                    onChange={updateSource}
                                    query={query}
                                />
                            </div>
                        </div>
                        : null
                }
            </div>
        </div>

        );
    }
    function renderParamDetails() {
        const param = (query?.params || []).find(s => s.id == selectedStep);
        if (!param) return null;

        const { errors } = validateParam(query, param)

        function updateParamDetails(field: string, value: any) {
            const _query = Object.assign({}, query);
            const index = (_query?.params).findIndex(s => s.id == selectedStep)
            if (index < 0) return

            (param as any)[field] = value;
            _query.params.splice(index, 1, param)
            onChange({ ..._query });
        }

        return (<div className='lucy-query-engine-step-configurator-container'>
            <div className="lucy-query-engine-step-configurator-header">
                <div className={classNames("query-editor-step-type-ico-container")}>
                    <FontAwesomeIcon icon={['fas', 'sliders-h']} />
                </div>
                Parameter
            </div>
            <div className="query-editor-configuration-message-container">


                <FormSection
                    label='Name'
                    error={errors?.name}
                >
                    <Input
                        value={param?.name || ''}
                        onChange={v => { updateParamDetails('name', v) }}
                    />
                </FormSection>
                <FormSection
                    label='Type'
                    error={errors?.type}
                >
                    <Select
                        selected={param?.type || ''}
                        onChange={(v) => { updateParamDetails('type', v) }}
                        options={[
                            { label: 'Text', value: 'string' },
                            { label: 'Number', value: 'number' },
                            { label: 'True/False', value: 'boolean' },
                        ]}
                    />
                </FormSection>
                <FormSection
                    label='Default Value'
                    error={errors?.defaultValue}
                >
                    <small><i>Default value will be used if a value is not passed </i></small>
                    <Input
                        value={param?.defaultValue || ''}
                        onChange={v => {
                            updateParamDetails('defaultValue', v)
                        }}
                    />
                </FormSection>

            </div>
        </div>

        );
    }

    function handleEdit() {
        const queryId = (query?.sources?.[0]?.id || '').split('/')?.[1] || null;
        if (!queryId) return;

        const popup = window.open(
            `/apps/lucy/canvas/queries?edit=${queryId}`,
            '_blank',
            'width=800,height=600'
        );

        const interval = setInterval(() => {
            if (!popup || popup.closed) {
                clearInterval(interval);
                props.onUpdateSavedQuery(queryId);
            }
        }, 500);
    }

    function renderInitialSource() {

        const initialSource = sources?.[0] || null

        return <>
            <div
                className={classNames("query-editor-step-container", "has-connector", {
                    'invalid': !initialSource,
                    'selected': selectedStep == InitialSourceId,
                    'source-only': sourceOnly
                })}
                onClick={() => setSelectedStep(InitialSourceId)}
            >
                <div className='query-editor-step-number'>{'1'}</div>

                <div className={classNames("query-editor-step-type-ico-container fontawesome-icon")}>
                    {renderIcon(initialSource?.type)}
                </div>

                <div className="query-editor-step-label">
                    {initialSource ? initialSource.name : '-- Select a source --'}
                </div>
            </div >
            {sourceOnly ?
                <div className="query-editor-edit-query-step">
                    <div className="message">
                        This is a saved query. Click here to edit it. Editing will affect all instances where this query is used. Proceed with caution.
                    </div>
                    <Button icon="fas pencil" title="Edit Query" onClick={handleEdit} />
                </div>
                : null}
        </>
    }

    function renderStep(s: IStep, i: number) {

        const step = getRegisteredStepByType(s.type);
        const selected = selectedStep == s.id
        const { isValid, errors } = validateStep(query, s)

        const icon = isValid ? step.icon : errorIcon
        const iconStyles = isValid ? { ...step?.iconStyles } : {}

        return <div
            className={classNames("query-editor-step-container", "has-connector", {
                'selected': selected,
                'invalid': !isValid,
            })}
            onClick={() => {
                setSelectedStep(s.id)
            }}
        >
            <div className='query-editor-step-number'>{(i + 2)}</div>

            <div className={classNames("query-editor-step-type-ico-container")}>
                <div className="step-icon-contianer" style={{ ...iconStyles, backgroundImage: `url(${icon})` }} ></div>
            </div>
            <div className="query-editor-step-label">
                {s.name}

            </div>
            <div className="query-editor-step-remover"
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    removeStep(s)
                }}
            >
                <FontAwesomeIcon icon={['fas', 'times']} />
            </div>
        </div>

    }

    async function downloadCsv() {
        try {
            await exportCsv(context, query);
        } catch (e) {
            alert('An error occurred while exporting data. Try again after a little while?');
        }
    }
    function renderPreview() {

        if (loadingStepData) return <Loading />
        if (hasChanged.current) return <Loading message='Please complete the configuration to preview data...' />

        if (!!selectedStepDataError) {
            return <div className="lucy-query-builder-query-editor-preview">
                <div className="lucy-query-builder-query-editor-preview-error">
                    <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} className="error-icon" />
                    <div className="error-title">Unable to retrieve data</div>
                    <div className="error-message">{selectedStepDataError}</div>
                </div>
            </div>
        }

        const columns = selectedStepData && selectedStepData.length > 0
            ? Object.keys(selectedStepData[0]).map(key => ({ id: key, label: key })).filter(a => hasValue(a.id))
            : [];
        let schemaProvider: { schema: ISourceSchema } = null;
        if (selectedStep == InitialSourceId) {
            schemaProvider = props.query.sources[0];
        } else {

            schemaProvider = props.query.steps.find(x => x.id == selectedStep);
        }

        return <div className="lucy-query-builder-query-editor-preview has-header">
            <div className="lucy-query-builder-query-editor-preview-header">
                <div className="lucy-query-builder-query-editor-preview-header-title">Preview</div>
                <div className="lucy-query-builder-query-editor-preview-header-actions">
                    <AsyncButton styles={{ margin: '0px 20px' }} title="Export" icon="fas download" onClick={downloadCsv} className="lucy-query-builder-exporter">Export</AsyncButton>

                    <div className="preview-mode-toggle-button-container">
                        <div className={classNames("preview-mode-button", { 'active': previewMode == 'table' })}
                            onClick={() => { setPreviewMode('table') }}
                        >
                            <FontAwesomeIcon icon={['fas', 'table']} />
                        </div>
                        <div className={classNames("preview-mode-button", { 'active': previewMode == 'bar-chart' })}
                            onClick={() => { setPreviewMode('bar-chart') }}
                        >
                            <FontAwesomeIcon icon={['fas', 'chart-bar']} />
                        </div>
                        <div className={classNames("preview-mode-button", { 'active': previewMode == 'pie-chart' })}
                            onClick={() => { setPreviewMode('pie-chart') }}
                        >
                            <FontAwesomeIcon icon={['fas', 'chart-pie']} />
                        </div>

                    </div>
                </div>
            </div>
            {previewMode == 'table' && <div className="lucy-query-builder-query-editor-preview-body">
                <Table
                    columns={columns}
                    data={selectedStepData || []}
                />
            </div>}
            {previewMode == 'pie-chart' && <div className='lucy-query-builder-query-editor-preview-body'>
                <PieChartPreview schema={schemaProvider?.schema} context={context} data={selectedStepData || []} />
            </div>
            }
            {previewMode == 'bar-chart' && <div className='lucy-query-builder-query-editor-preview-body'>
                <BarChartPreview schema={schemaProvider?.schema} context={context} data={selectedStepData || []} />
            </div>
            }
        </div>
    }

    function renderPipeline() {
        return <>
            <div className='lucy-query-builder-query-editor-details-section-title'>Pipeline</div>
            <div className="lucy-query-builder-query-editor-details-section-content">
                {renderInitialSource()}

                <SortableList
                    items={steps}
                    renderItem={(step: IStep, i: number) => renderStep(step, i)}
                    onSort={(sorted) => { onChange({ ...query, steps: sorted }) }}
                />

                {!sourceOnly ?
                    <StepSelector
                        onSelect={handleStepSelector}
                    />
                    : null}
            </div>
        </>
    }

    function renderDataSources() {
        return <>
            <div className='lucy-query-builder-query-editor-details-section-title'>Data Sources</div>
            <div className="lucy-query-builder-query-editor-details-section-content">
                {query?.sources?.map((s, i) => {
                    return <div
                        className={classNames("query-editor-step-container", {
                            'selected': selectedStep == s.id,
                        })}
                        onClick={() => {
                            setSelectedStep(s.id)
                        }}
                        key={i}
                    >
                        <div className={classNames("query-editor-step-type-ico-container")}>
                            <FontAwesomeIcon icon={['fas', 'database']} />
                        </div>
                        <div className="query-editor-step-label">
                            {s.name}
                        </div>

                        {i > 0 &&
                            <div className="query-editor-step-remover"
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    removeSource(s)
                                }}
                            >
                                <FontAwesomeIcon icon={['fas', 'times']} />
                            </div>
                        }
                    </div>
                })}
            </div>
        </>
    }

    function addNewParam() {
        const params = (query?.params || [])

        const id = 'p-' + (new Date()).getTime();
        params.push({
            id: id,
            name: '',
            type: 'string',
        })
        onChange({ ...query, params: params })
        nextStepIdRef.current = id
    }

    function renderParams() {
        return <>
            <div className='lucy-query-builder-query-editor-details-section-title'>Parameters</div>
            <div className="lucy-query-builder-query-editor-details-section-content">

                {!query?.params || query?.params?.length == 0
                    ? <div className="lucy-query-builder-query-editor-details-section-no-content">
                        You don't have any parameters configured. <br />
                        Click on the `Add Parameter` button to add one
                    </div>
                    : null}

                {query?.params?.map((s, i) => {

                    const { isValid } = validateParam(query, s)
                    return <div
                        className={classNames("query-editor-step-container", {
                            'selected': selectedStep == s.id,
                            'invalid': !isValid
                        })}
                        onClick={() => {
                            setSelectedStep(s.id)
                        }}
                        key={i}
                    >
                        <div className={classNames("query-editor-step-type-ico-container")}>
                            <FontAwesomeIcon icon={['fas', 'sliders-h']} />
                        </div>
                        <div className="query-editor-step-label">
                            {s.name}
                        </div>

                        <div className="query-editor-step-remover"
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                removeParam(s)
                            }}
                        >
                            <FontAwesomeIcon icon={['fas', 'times']} />
                        </div>

                    </div>
                })}

                <div className='query-editor-step-selector-button-contianer'>
                    <div className="query-editor-step-selector-button" onClick={addNewParam} >-- Add Paramerer --</div>
                </div>
            </div>
        </>
    }

    const { isValid } = validateQuery(query)
    const isParamsValid = validateParams(query)

    return <div className="lucy-query-builder-query-editor-component-container">

        <div className="lucy-query-builder-query-editor-details-container">
            <div className="lucy-query-builder-query-editor-details-content">
                {selectedTab == 'pipeline' && renderPipeline()}
                {selectedTab == 'datasources' && renderDataSources()}
                {selectedTab == 'params' && renderParams()}
            </div>

            {!sourceOnly && supportParams
                ? <div className="lucy-query-builder-query-editor-details-footer">
                    <div className={classNames(
                        "lucy-query-builder-query-editor-details-footer-button",
                        {
                            "active": selectedTab == 'pipeline',
                            "invalid": !isValid,
                        }
                    )}
                        onClick={() => {
                            setSelectedTab('pipeline');
                            setSelectedStep(query?.steps?.[query?.steps?.length - 1]?.id || InitialSourceId);
                        }}
                    >
                        <FontAwesomeIcon icon={['fas', 'project-diagram']} />
                    </div>
                    <div className={classNames(
                        "lucy-query-builder-query-editor-details-footer-button",
                        {
                            "active": selectedTab == 'datasources'
                        }
                    )}
                        onClick={() => {
                            setSelectedTab('datasources');
                            setSelectedStep(query?.sources?.[0]?.id)
                        }}
                    >
                        <FontAwesomeIcon icon={['fas', 'database']} />
                    </div>
                    <div className={classNames(
                        "lucy-query-builder-query-editor-details-footer-button",
                        {
                            "active": selectedTab == 'params',
                            'invalid': !isParamsValid
                        }
                    )}
                        onClick={() => {
                            setSelectedTab('params');
                            setSelectedStep((query?.params || [])?.[0]?.id)
                        }}
                    >
                        <FontAwesomeIcon icon={['fas', 'sliders-h']} />
                    </div>
                </div>
                : null}
        </div>

        <div className="lucy-query-builder-query-editor-content-container">
            {(!sourceOnly && selectedTab == 'pipeline') ?
                <Copilot query={query} context={context} onQueryChanged={(q) => {
                    if (q?.steps) {
                        setSelectedStep('');
                        props.onChange(q);
                    }
                }} />
                : null}
            <div className="lucy-query-builder-query-editor-configuration-container">
                {selectedTab == 'pipeline' && <>
                    {renderInitialSourceMessage()}
                    {renderConfiguration()}
                </>}
                {selectedTab == 'datasources' && renderSourceDetails()}
                {selectedTab == 'params' && renderParamDetails()}

            </div>

            <div className="lucy-query-builder-query-editor-preview-container">
                {renderPreview()}
            </div>

        </div>

        {showSourceBrowser && (
            <DataSourceBrowserModal
                show={true}
                onClose={() => setShowSourceBrowser(false)}
                source={null}
                onSelect={changeSource}
                showCloseButton={true}
                origins={[]}
                query={query}
            />
        )}

    </div >

}
